import API, { Types } from '@/modules/API';
import { REPORTING_QUERY_VERSION, setDeviceCommonFields } from '@/store/modules/Reports';
import { formatModified, getModifiedSeconds } from '@/views/services/cabinetDetailsApi';

/**
 * FIXTURE device-tabs data service.
 *
 * Mirrors the Cabinet Details pattern (see cabinetDetailsApi.ts): every tab's
 * properties AND streams are fetched together in a single streaming_reporting
 * query (one POST, many columns) instead of N separate /properties and
 * /streams/.../latest calls. Each `<column>__modified` carries the last-changed
 * epoch-seconds for that property/stream, replacing the old per-stream
 * `/latest/` "since" lookups.
 *
 * Only the Overview ("full view") tab is fetched eagerly on device click; every
 * other tab fetches its columns lazily on open and caches the result per device
 * with the same in-memory TTL used by Cabinet Details (15 min).
 */

const STREAM_ITEMS_LIMIT = 999999999;

// Re-export so consumers can format/read __modified values from one place.
export { formatModified, getModifiedSeconds };

/**
 * Eager columns fetched on device click for the Overview ("full view") tab.
 * `power`/`version_nrf52840` are streams; `meta.device`/`device.fixture` are
 * properties. `device.dimmer_converting_table` is fetched separately via the
 * cached single-property API (fetchDevicePropertyCached). Fields already present
 * on the reportsList device row (sys___active, sys___connected, name, meta.imsi,
 * meta.part_number, device.hardwareid, ...) are NOT requested again.
 */
export const FIXTURE_HEADER_COLUMNS = [
  'id',
  'power',
  'power__modified',
  'version_nrf52840',
  'meta.device',
  // Near-static; fetched eagerly once per device-open so the Control tab needs
  // no network and the Advanced tab fetches one fewer column. Lives in
  // fixtureRow for the whole device session.
  'device.fixture'
];

/**
 * Per-tab lazy column sets. Each is fetched once per device/tab and cached.
 * Overview is covered by FIXTURE_HEADER_COLUMNS; device-data and dali need no
 * reporting columns (store-only / MQTT-only).
 */
export const FIXTURE_TAB_COLUMNS: Record<string, string[]> = {
  // Control needs no reporting columns: device.fixture is eager (header) and
  // power/dimmer come from the Overview fetch.
  'tab-control': [],
  'tab-energy': [
    'power_consumption',
    'power_consumption__modified',
    'voltage',
    'voltage__modified',
    'current',
    'current__modified',
    'power_factor',
    'power_factor__modified'
  ],
  'tab-advanced': [
    'device.hardwareid',
    'device.schedule',
    'meta.provider_id',
    'device.config',
    'meta.ipaddress',
    'dashboard.stream_commands',
    'meta.driver',
    'cellid',
    'cellid__modified',
    'rssi',
    'rssi__modified',
    'sys___connected',
    'sys___connected__modified',
    'sys___active',
    'sys___active__modified',
    'cellular_status'
  ],
  'tab-temperature': ['modem_temperature', 'modem_temperature__modified'],
  'tab-curve': ['device.schedule']
};

/**
 * Stream-shaped columns the tabs consume. Used to build a `deviceStreams`
 * array (with `last_modified` from the matching `__modified` column) so the
 * existing child tab components keep working unchanged.
 */
export const FIXTURE_STREAM_COLUMN_NAMES = [
  'power',
  'version_nrf52840',
  'power_consumption',
  'voltage',
  'current',
  'power_factor',
  'cellid',
  'rssi',
  'sys___connected',
  'sys___active',
  'cellular_status',
  'modem_temperature'
];

/**
 * Property-shaped columns. Used to build a `propsList.results` array so tab
 * components that read `propsList.results.find((el) => el.name === '...')`
 * continue to work without changes.
 */
export const FIXTURE_PROPERTY_COLUMN_NAMES = [
  'meta.device',
  'device.fixture',
  'device.hardwareid',
  'device.schedule',
  'meta.provider_id',
  'device.config',
  'meta.ipaddress',
  'dashboard.stream_commands',
  'meta.driver'
];

const reportingPath = (companyId: string, projectId: string) =>
  `/${companyId}/projects/${projectId}/streaming_reporting_query/`;

interface FetchParams {
  companyId: string;
  projectId: string;
  defaultLocation?: any;
}

/**
 * Generic single-row reporting query for a fixture by id with caller-provided
 * columns. Returns the row (with common field defaults applied) or null.
 */
export async function fetchFixtureColumns(
  params: FetchParams & { id: string; columns: string[] }
): Promise<any | null> {
  const { companyId, projectId, id, columns, defaultLocation } = params;
  const body: any = {
    columns,
    where: [`id='${id}'`],
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  };
  const result = await API.streamingPost(
    Types.COMPANIES,
    reportingPath(companyId, projectId),
    body,
    {}
  );
  const list = Array.isArray(result) ? result : [];
  if (!list.length) return null;
  const row = list[0];
  setDeviceCommonFields(row, defaultLocation);
  return row;
}

/* -------------------------------------------------------------------------- */
/* Per-tab in-memory cache (mirrors cabinetDetailsApi TTL behaviour)          */
/* -------------------------------------------------------------------------- */

/**
 * 15-min TTL, matching Cabinet Details. Cached rows survive device switches
 * (keyed by deviceId::tab) so quick back-and-forth flows avoid redundant
 * reporting queries. Live streams keep updating via MQTT after the seed.
 */
export const FIXTURE_TAB_CACHE_TTL_MS = 10 * 60 * 1000;

interface TabCacheEntry {
  row: any;
  fetchedAt: number;
}

const tabCache: Map<string, TabCacheEntry> = new Map();

const tabKey = (deviceId: string, tabName: string) => `${deviceId}::${tabName}`;

/** True if a cached row for (deviceId, tabName) exists and is within TTL. */
export function isFixtureTabSnapshotFresh(deviceId: string, tabName: string): boolean {
  const entry = tabCache.get(tabKey(deviceId, tabName));
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < FIXTURE_TAB_CACHE_TTL_MS;
}

/** Returns the cached row for (deviceId, tabName), or null. */
export function getFixtureTabSnapshot(deviceId: string, tabName: string): any | null {
  const entry = tabCache.get(tabKey(deviceId, tabName));
  if (entry) touchDevice(deviceId);
  return entry ? entry.row : null;
}

/** Cache the row for (deviceId, tabName) with a fresh timestamp. */
export function primeFixtureTabSnapshot(deviceId: string, tabName: string, row: any): void {
  if (!deviceId || !tabName || !row) return;
  tabCache.set(tabKey(deviceId, tabName), { row, fetchedAt: Date.now() });
  touchDevice(deviceId);
}

/** Drop all cached tab rows for a device (e.g. after a user-initiated save). */
export function invalidateFixtureTabCache(deviceId: string): void {
  if (!deviceId) return;
  const prefix = `${deviceId}::`;
  Array.from(tabCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) tabCache.delete(k);
  });
  // Drop any cached single-properties for this device too.
  const propPrefix = `${deviceId}::`;
  Array.from(propertyCache.keys()).forEach((k) => {
    if (k.startsWith(propPrefix)) propertyCache.delete(k);
  });
}

/* -------------------------------------------------------------------------- */
/* Single-property cache (same TTL) — for related-device props the tab needs  */
/* outside the per-fixture reporting query, e.g. a linked cabinet's           */
/* meta.devices_schedule fetched by the Schedule (curve) tab.                 */
/* -------------------------------------------------------------------------- */

interface PropertyCacheEntry {
  value: any;
  fetchedAt: number;
}

const propertyCache: Map<string, PropertyCacheEntry> = new Map();

const propKey = (deviceId: string, propertyName: string) => `${deviceId}::${propertyName}`;

/**
 * Fetch a single device property with the same 15-min TTL as the tab cache.
 * Returns the first matching property result (or null). Repeated tab opens
 * within the TTL reuse the cached value instead of hitting the API again.
 */
export async function fetchDevicePropertyCached(deviceId: string, propertyName: string): Promise<any> {
  if (!deviceId || !propertyName) return null;
  const key = propKey(deviceId, propertyName);
  const entry = propertyCache.get(key);
  if (entry && Date.now() - entry.fetchedAt < FIXTURE_TAB_CACHE_TTL_MS) {
    touchDevice(deviceId);
    return entry.value;
  }
  try {
    const response = await API.get(Types.DEVICES, `${deviceId}/properties`, { name: propertyName });
    const result = (response && response.results && response.results[0]) || null;
    propertyCache.set(key, { value: result, fetchedAt: Date.now() });
    touchDevice(deviceId);
    return result;
  } catch (e) {
    return null;
  }
}

/** Drop a cached single-property (e.g. after that property is saved). */
export function invalidateDevicePropertyCache(deviceId: string, propertyName?: string): void {
  if (!deviceId) return;
  if (propertyName) {
    propertyCache.delete(propKey(deviceId, propertyName));
    return;
  }
  const prefix = `${deviceId}::`;
  Array.from(propertyCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) propertyCache.delete(k);
  });
}

/* -------------------------------------------------------------------------- */
/* Bounded-memory LRU — cap how many distinct devices we retain cached data    */
/* for, so clicking through a large project doesn't grow the caches forever.   */
/* The least-recently-used device's tab + property entries are evicted.        */
/* -------------------------------------------------------------------------- */

const MAX_CACHED_DEVICES = 50;

// Device ids ordered least-recently-used first, most-recently-used last.
const deviceLru: string[] = [];

function evictDevice(deviceId: string): void {
  const prefix = `${deviceId}::`;
  Array.from(tabCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) tabCache.delete(k);
  });
  Array.from(propertyCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) propertyCache.delete(k);
  });
}

function touchDevice(deviceId: string): void {
  if (!deviceId) return;
  const i = deviceLru.indexOf(deviceId);
  if (i !== -1) deviceLru.splice(i, 1);
  deviceLru.push(deviceId);
  while (deviceLru.length > MAX_CACHED_DEVICES) {
    const evicted = deviceLru.shift();
    if (evicted) evictDevice(evicted);
  }
}
